import * as Sentry from '@sentry/react';
import {
  collectDomIntegrityTags,
  isDomMutationCrash,
  DOM_MUTATION_CRASH_TAG,
} from './domIntegrity';

export interface SentryConfig {
  dsn: string;
  environment: string;
  appName: string;
  release?: string;
  enablePerformanceMonitoring?: boolean;
  enableSessionTracking?: boolean;
  /**
   * Drops the Session Replay integration entirely.
   *
   * `enableSessionTracking: false` only zeroes `replaysSessionSampleRate`;
   * `replaysOnErrorSampleRate` stays at 1.0, so the integration keeps buffering
   * DOM mutations in memory for every session in case an error shows up. That
   * buffer is a real cost on memory-constrained surfaces — an iOS Home Screen
   * PWA gets a far smaller allowance than the same page in a Safari tab, and a
   * long-lived admin session full of table rows can be pushed into a jetsam
   * kill that leaves no JS error behind. Set this to opt a surface out of the
   * buffer altogether.
   */
  disableSessionReplay?: boolean;
  enableProfiling?: boolean; // Enable performance profiling
  enableConsoleLogging?: boolean;
  tracesSampleRate?: number;
  profileSessionSampleRate?: number; // Profile sampling rate (0.0 to 1.0)
  beforeSend?: (event: Sentry.Event, hint?: Sentry.EventHint) => Sentry.Event | null;
  securityToken?: string; // Optional security token for source map access
  securityTokenHeader?: string; // Optional security token header name (default: X-Sentry-Token)
}

export interface UserContext {
  id: string;
  email?: string;
  username?: string;
  companyId?: string;
  locationId?: string;
  role?: string;
}

/**
 * Initialize Sentry for frontend applications
 */
export function initSentry(config: SentryConfig): void {
  const {
    dsn,
    environment,
    appName,
    release,
    enablePerformanceMonitoring = true,
    enableSessionTracking = true,
    disableSessionReplay = false,
    enableProfiling = false, // Profiling disabled by default to reduce overhead
    enableConsoleLogging = true,
    tracesSampleRate = environment === 'production' ? 0.1 : 1.0,
    profileSessionSampleRate = environment === 'production' ? 0.1 : 1.0, // Profile 10% in prod, 100% in dev
    beforeSend,
    // Note: securityToken and securityTokenHeader are not used for error submission
    // They are only for source map fetching (when Sentry requests maps from your server)
  } = config;

  const fullCaptureEnabled =
    (typeof import.meta !== 'undefined' && Boolean((import.meta as any)?.env?.VITE_SENTRY_FULL_CAPTURE === 'true')) ||
    (typeof process !== 'undefined' && process.env?.VITE_SENTRY_FULL_CAPTURE === 'true');

  // Don't initialize if DSN is not provided
  if (!dsn || dsn === '') {
    console.warn(`[Sentry] DSN not provided for ${appName}, skipping initialization`);
    return;
  }

  // Log Sentry initialization for debugging
  if (environment !== 'production') {
    console.log(`[Sentry] Initializing for ${appName}`, {
      environment,
      dsn: dsn.substring(0, 20) + '...', // Log partial DSN for verification
      release,
    });
  }

  Sentry.init({
    dsn,
    environment,
    release,
    
    // Setting this option to true will send default PII data to Sentry
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    
    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration({
        // Set tracing origins to track performance
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
          'localhost', 
          /^https:\/\/.*\.web\.app/, 
          /^https:\/\/api-.*\.web\.app/,
          /^https:\/\/.*\.run\.app/,
          /^https:\/\/api-.*\.run\.app/,
        ],
      } as any), // Type assertion needed due to TypeScript version compatibility
      // Browser Profiling Integration - enables performance profiling
      // Requires @sentry/react version 7.60.0 or higher
      ...(enableProfiling ? [Sentry.browserProfilingIntegration()] : []),
      ...(disableSessionReplay
        ? []
        : [
            Sentry.replayIntegration({
              // Session Replay
              maskAllText: true,
              blockAllMedia: true,
            }),
          ]),
      // Console logging integration - send console.log, console.warn, and console.error calls as logs to Sentry
      // Requires @sentry/react version 9.41.0 or higher
      ...(enableConsoleLogging
        ? [Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] })]
        : []),
      // React Router integration will be added per-app if needed
    ],

    // Enable logs to be sent to Sentry
    // Requires @sentry/react version 9.41.0 or higher
    enableLogs: enableConsoleLogging,

    // Performance Monitoring
    tracesSampleRate: enablePerformanceMonitoring ? (fullCaptureEnabled ? 1.0 : tracesSampleRate) : 0,
    
    // Performance Profiling
    // Set profileSessionSampleRate to control profiling frequency
    // 1.0 = profile every session, 0.1 = profile 10% of sessions
    profileSessionSampleRate: enableProfiling ? profileSessionSampleRate : 0,
    
    // Session Replay
    replaysSessionSampleRate: disableSessionReplay
      ? 0
      : enableSessionTracking
        ? (fullCaptureEnabled ? 1.0 : (environment === 'production' ? 0.1 : 1.0))
        : 0,
    replaysOnErrorSampleRate: disableSessionReplay ? 0 : 1.0,

    // Error filtering
    beforeSend(event, hint) {
      // Who else has been editing our DOM. Written onto the event rather than via
      // Sentry.setTag, because the scope was already applied when this event was created —
      // from in here, setTag only ever reaches some *later* event.
      //
      // This is what distinguishes a React bug from an external rewriter for the whole
      // `removeChild` / `insertBefore` family of crashes (PRODUCTION-J2, D8, DP and friends),
      // so every app that reports through this bootstrap gets it, not just the widget.
      event.tags = { ...event.tags, ...collectDomIntegrityTags() };

      // Give the DOM-reparenting crash family one searchable name. The same fault appears
      // under four different messages across engines and operations, scattered over a dozen
      // Sentry issues in three apps; `dom_mutation_crash:true` finds them all at once.
      const rawMessage =
        (hint?.originalException as { message?: unknown } | undefined)?.message ??
        event.exception?.values?.[0]?.value ??
        event.message;
      if (isDomMutationCrash(rawMessage)) {
        event.tags[DOM_MUTATION_CRASH_TAG] = 'true';
      }

      // Filter out known non-critical errors
      if (event.exception) {
        const error = hint.originalException;
        if (error instanceof Error) {
          // Filter out network errors that are expected
          if (error.message.includes('Failed to fetch') || 
              error.message.includes('NetworkError') ||
              error.message.includes('ERR_NETWORK')) {
            // Only capture if it's not a common network issue
            return null;
          }
        }
      }

      // Apply custom beforeSend if provided
      if (beforeSend) {
        const result = beforeSend(event, hint);
        return result as typeof event | null;
      }

      return event;
    },

    // Note: Security tokens are NOT sent with error submissions
    // They are only used when Sentry fetches source maps from your server
    // Sending them here causes CORS errors because Sentry's API doesn't accept custom headers

    // Ignore errors from browser extensions
    ignoreErrors: [
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      'conduitPage',
    ],

    // Don't send errors in development unless explicitly enabled
    // Note: In browser context, we check if DSN is set instead of process.env
    enabled:
      dsn !== '' &&
      (environment !== 'development' ||
        (typeof process !== 'undefined' && process.env?.VITE_SENTRY_ENABLE_IN_DEV === 'true')),
  });

  // Set app name as tag
  Sentry.setTag('app', appName);
}

/**
 * Tag the current Host (FOH) UI surface so Session Replay and issues are searchable
 * (e.g. filter: host_surface:split_view).
 */
export function setHostAppSurfaceContext(surface: string | null): void {
  if (surface) {
    Sentry.setTag('host_surface', surface);
    Sentry.setContext('host_ui', { surface });
  } else {
    Sentry.setTag('host_surface', undefined);
    Sentry.setContext('host_ui', null);
  }
}

/**
 * Start Session Replay for the current page load, independent of session sample rate.
 * Use for high-signal areas (e.g. Split View) where default sampling under-represents traffic.
 */
export function startSessionReplayNow(context?: { surface?: string; reason?: string }): void {
  const { surface, reason } = context ?? {};
  if (surface) {
    setHostAppSurfaceContext(surface);
  }
  Sentry.addBreadcrumb({
    category: 'session',
    message: reason ?? 'session_replay_manual_start',
    level: 'info',
    data: surface ? { host_surface: surface } : undefined,
  });
  try {
    Sentry.getReplay()?.start();
  } catch {
    // Replay disabled or not yet initialized
  }
}

/** Merged widget host/mode for Sentry tags + context (filter / Replay). */
let widgetRuntimeState: Record<string, string> = {};

/**
 * Tag widget load context so Sentry issues can be filtered by shell (microsite vs embed),
 * mode (bubble / embedded / standalone), and page.
 * Safe to call multiple times; later calls merge over earlier keys.
 */
export function setWidgetRuntimeTags(partial: Record<string, string | undefined | null>): void {
  for (const [k, v] of Object.entries(partial)) {
    if (v === undefined || v === null || v === '') continue;
    widgetRuntimeState[k] = String(v);
  }

  const tagKeys = [
    'widget_shell',
    'widget_mode',
    'widget_page',
    'widget_multi_location',
    'location_id',
  ] as const;
  for (const key of tagKeys) {
    const v = widgetRuntimeState[key];
    if (v !== undefined) {
      Sentry.setTag(key, v);
    }
  }

  Sentry.setContext('widget_runtime', { ...widgetRuntimeState });
}

/**
 * Set user context in Sentry
 */
export function setUserContext(user: UserContext | null): void {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
    });

    // Set additional context as tags
    if (user.companyId) {
      Sentry.setTag('companyId', user.companyId);
    }
    if (user.locationId) {
      Sentry.setTag('locationId', user.locationId);
    }
    if (user.role) {
      Sentry.setTag('role', user.role);
    }
  } else {
    // Clear user context
    Sentry.setUser(null);
    Sentry.setTag('companyId', undefined);
    Sentry.setTag('locationId', undefined);
    Sentry.setTag('role', undefined);
  }
}

/**
 * Set company context
 */
export function setCompanyContext(companyId: string | null): void {
  if (companyId) {
    Sentry.setTag('companyId', companyId);
    Sentry.setContext('company', { id: companyId });
  } else {
    Sentry.setTag('companyId', undefined);
    Sentry.setContext('company', null);
  }
}

/**
 * Set location context
 */
export function setLocationContext(locationId: string | null): void {
  if (locationId) {
    Sentry.setTag('locationId', locationId);
    Sentry.setContext('location', { id: locationId });
  } else {
    Sentry.setTag('locationId', undefined);
    Sentry.setContext('location', null);
  }
}

/**
 * Manually capture an error
 */
export function captureError(error: unknown, context?: Record<string, unknown>): string {
  const normalizedError =
    error instanceof Error
      ? error
      : new Error(typeof error === 'string' ? error : 'Unknown error');
  if (context) {
    Sentry.setContext('error_context', context);
  }
  return Sentry.captureException(normalizedError);
}

/**
 * Capture a message
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info'): string {
  return Sentry.captureMessage(message, level);
}

/** Phases where the bubble/floating widget submits a reservation, payment init, waitlist, or parses success bodies. Use for alerting. */
export type WidgetBookingSubmitPhase =
  | 'reservation_confirm_http'
  | 'deposit_experience_init_http'
  | 'deposit_per_guest_init_http'
  | 'waitlist_http'
  | 'reservation_success_body_parse'
  | 'waitlist_success_body_parse'
  /** fetch() rejected — the reply never arrived, so the booking's fate is unknown. */
  | 'reservation_confirm_network'
  /** Our own client-side deadline fired before the backend answered. */
  | 'reservation_confirm_timeout'
  /** After a network/timeout/5xx failure, the attempt-status check said nothing was booked. */
  | 'reservation_unrecoverable'
  | 'submission_uncaught';

/**
 * High-severity telemetry when the booking widget Confirm path fails after talking to our API / parsing responses.
 *
 * Attach Sentry ISSUE alerts to tag `feature:widget_booking_submit` (e.g. email or Slack) so production failures are visible.
 * Context includes HTTP status and raw/parsed response for backend investigation — may contain PII; restrict alert routing as needed.
 */
export function captureWidgetBookingSubmitFailure(
  error: unknown,
  context: {
    phase: WidgetBookingSubmitPhase;
    location_id?: string;
    http_status?: number;
    request_url?: string;
    response_body_truncated?: string;
    backend_error_payload?: Record<string, unknown>;
    backend_error_code?: string;
    /** Message shown inline under Confirm */
    guest_facing_message?: string;
    party_size?: number;
    date_iso?: string;
    time?: string;
    /** Widget-only field for dashboards */
    experience_id?: string;
  },
): string {
  const normalizedError =
    error instanceof Error
      ? error
      : new Error(typeof error === 'string' ? error : 'Widget booking submission failed');

  let truncated = context.response_body_truncated;
  if (truncated && truncated.length > 8000) {
    truncated = truncated.slice(0, 8000) + '… [truncated]';
  }

  return Sentry.captureException(normalizedError, {
    level: 'error',
    tags: {
      feature: 'widget_booking_submit',
      booking_submit_phase: context.phase,
      ...(context.location_id ? { location_id: context.location_id } : {}),
      ...(context.backend_error_code ? { backend_error_code: context.backend_error_code } : {}),
      ...(context.http_status !== undefined ? { http_status: String(context.http_status) } : {}),
    },
    contexts: {
      widget_booking_submit: {
        ...context,
        response_body_truncated: truncated ?? context.response_body_truncated,
      },
    },
  });
}

/** What the widget concluded after a Confirm request failed without a usable answer. */
export type WidgetBookingRecoveryOutcome =
  /** The reservation existed after all — the guest sees their confirmation, not an error. */
  | 'recovered_reservation'
  /** The backend replayed an existing booking for this attempt id — a double-book was avoided. */
  | 'idempotent_replay'
  /** Verified that nothing was created; the guest can safely try again. */
  | 'confirmed_not_created'
  /** Still in flight when we stopped waiting. */
  | 'still_in_progress'
  /** The status check itself could not answer. */
  | 'unknown';

/**
 * The moment the widget could not tell whether a booking succeeded, and what it did about it.
 *
 * This is the event to watch for the "guest saw an error but was actually booked" complaint:
 * alert on tag `feature:widget_booking_recovery`, then split by `recovery_outcome`.
 * `recovered_reservation` counts double bookings that would have happened before this existed;
 * `unknown` and `still_in_progress` are the cases still worth chasing.
 */
export function captureWidgetBookingRecovery(context: {
  outcome: WidgetBookingRecoveryOutcome;
  /** Why the original request was inconclusive (network error, timeout, HTTP 502, …). */
  trigger: string;
  client_request_id?: string;
  location_id?: string;
  reservation_id?: string;
  http_status?: number;
  attempt_count?: number;
  elapsed_ms?: number;
  party_size?: number;
  date_iso?: string;
  time?: string;
  original_error?: string;
}): string {
  const level: Sentry.SeverityLevel =
    context.outcome === 'recovered_reservation' || context.outcome === 'idempotent_replay'
      ? 'warning'
      : context.outcome === 'confirmed_not_created'
        ? 'info'
        : 'error';

  return Sentry.captureMessage(`Widget booking recovery: ${context.outcome}`, {
    level,
    tags: {
      feature: 'widget_booking_recovery',
      recovery_outcome: context.outcome,
      recovery_trigger: context.trigger,
      ...(context.location_id ? { location_id: context.location_id } : {}),
      ...(context.http_status !== undefined ? { http_status: String(context.http_status) } : {}),
    },
    contexts: {
      widget_booking_recovery: { ...context },
    },
  });
}

/**
 * Wrap a function with Sentry transaction tracking
 */
export async function withTransaction<T>(
  name: string,
  op: string,
  fn: () => Promise<T>
): Promise<T> {
  return Sentry.startSpan(
    {
      name,
      op,
    },
    async () => {
      try {
        return await fn();
      } catch (error) {
        Sentry.captureException(error as Error);
        throw error;
      }
    }
  );
}

/**
 * Set breadcrumb for debugging
 */
export function addBreadcrumb(message: string, category?: string, level?: Sentry.SeverityLevel, data?: Record<string, unknown>): void {
  Sentry.addBreadcrumb({
    message,
    category: category || 'custom',
    level: level || 'info',
    data,
  });
}

/**
 * Track a custom business event (e.g., reservation created, user action)
 * Use this to track important business events in Sentry
 * 
 * @example
 * trackBusinessEvent('reservation.created', {
 *   reservationId: '123',
 *   locationId: 'loc-456',
 *   partySize: 4,
 *   source: 'widget'
 * });
 */
export function trackBusinessEvent(eventName: string, data?: Record<string, any>): void {
  Sentry.addBreadcrumb({
    category: 'business',
    message: eventName,
    level: 'info',
    data: data || {},
  });
  
  // Also send as a custom event for better visibility
  Sentry.captureMessage(eventName, {
    level: 'info',
    tags: {
      event_type: 'business',
    },
    extra: data || {},
  });
}

/**
 * Send a test log to Sentry for verification
 * This is useful for testing that Sentry logging is working correctly
 * Uses Sentry.logger.info() when available (v9.41.0+), falls back to captureMessage
 * 
 * @example
 * // In browser console or code:
 * import { sendTestLog } from '@shared/utils/sentry';
 * sendTestLog();
 * 
 * // Or with custom data:
 * sendTestLog({ custom_field: 'test_value' });
 */
export function sendTestLog(data?: Record<string, any>): string | void {
  const testData = {
    log_source: 'sentry_test',
    timestamp: new Date().toISOString(),
    ...data,
  };
  
  // Use Sentry.logger.info() if available (requires enableLogs: true and v9.41.0+)
  if (Sentry.logger && typeof Sentry.logger.info === 'function') {
    Sentry.logger.info('User triggered test log', testData);
    return;
  }
  
  // Fallback to captureMessage for older versions
  // Add breadcrumb for context
  Sentry.addBreadcrumb({
    category: 'test',
    message: 'User triggered test log',
    level: 'info',
    data: testData,
  });
  
  // Send as a message to Sentry
  return Sentry.captureMessage('User triggered test log', {
    level: 'info',
    tags: {
      log_source: 'sentry_test',
      test: 'true',
    },
    extra: testData,
  });
}

/**
 * Sentry Metrics API - Track custom metrics
 * Requires @sentry/react version 10.25.0 or higher
 * 
 * @example
 * // Count metric - track events (clicks, orders, API calls)
 * import { metrics } from '@shared/utils/sentry';
 * metrics.count('button_click', 1);
 * metrics.count('order_placed', 1, { product_type: 'reservation' });
 * 
 * // Gauge metric - track current values (queue depth, active users)
 * metrics.gauge('active_reservations', 42);
 * metrics.gauge('queue_depth', 5, { location_id: 'loc-123' });
 * 
 * // Distribution metric - track value ranges (response times, payload sizes)
 * metrics.distribution('api_response_time', 200);
 * metrics.distribution('page_load_time', 150, { page: 'dashboard' });
 */
export const metrics = {
  /**
   * Count metric - tracks the number of times something happened
   * Use for: clicks, orders, API calls, events
   * 
   * @param name - Metric name (e.g., 'button_click', 'order_placed')
   * @param value - Count value (default: 1)
   * @param tags - Optional tags for filtering/grouping
   */
  count: (name: string, value: number = 1, tags?: Record<string, string>) => {
    try {
      Sentry.metrics.count(name, value, tags);
    } catch (error) {
      console.warn('[Sentry Metrics] Failed to send count metric:', error);
    }
  },

  /**
   * Gauge metric - tracks the current value of something
   * Use for: queue depth, active connections, current users
   * 
   * @param name - Metric name (e.g., 'active_users', 'queue_depth')
   * @param value - Current gauge value
   * @param tags - Optional tags for filtering/grouping
   */
  gauge: (name: string, value: number, tags?: Record<string, string>) => {
    try {
      Sentry.metrics.gauge(name, value, tags);
    } catch (error) {
      console.warn('[Sentry Metrics] Failed to send gauge metric:', error);
    }
  },

  /**
   * Distribution metric - tracks the distribution of values
   * Use for: response times, payload sizes, durations
   * 
   * @param name - Metric name (e.g., 'response_time', 'page_load_time')
   * @param value - Value to record
   * @param tags - Optional tags for filtering/grouping
   */
  distribution: (name: string, value: number, tags?: Record<string, string>) => {
    try {
      Sentry.metrics.distribution(name, value, tags);
    } catch (error) {
      console.warn('[Sentry Metrics] Failed to send distribution metric:', error);
    }
  },
};
